import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class KioskRouteService {
	private readonly router = inject(Router);

	/**
	 * Checks whether the current URL belongs to the kiosk route namespace.
	 *
	 * @return {boolean} True when the active route is under `/kiosk`.
	 */
	public isKioskContext(): boolean {
		const currentPath = this.router.url.split('?')[0];
		return currentPath === '/kiosk' || currentPath.startsWith('/kiosk/');
	}

	/**
	 * Prefixes an application path with `/kiosk` when the current route is in kiosk context.
	 *
	 * @param {string} path - Application path such as `/requisitions`.
	 * @return {string} Path adjusted for the current route namespace.
	 */
	public resolveAppPath(path: string): string {
		const normalizedPath = path.startsWith('/') ? path : `/${path}`;
		if (this.isKioskContext() && !normalizedPath.startsWith('/kiosk')) {
			return `/kiosk${normalizedPath}`;
		}
		return normalizedPath;
	}

	/**
	 * Returns the sign-in path for the current route namespace.
	 *
	 * @return {string} `/kiosk/auth/sign-in` or `/auth/sign-in`.
	 */
	public getSignInPath(): string {
		return this.isKioskContext() ? '/kiosk/auth/sign-in' : '/auth/sign-in';
	}

	/**
	 * Returns the requisition detail path for the current route namespace.
	 *
	 * @param {number | string} requisitionId - Requisition identifier.
	 * @return {string} `/kiosk/requisition/{id}` or `/requisition/{id}`.
	 */
	public getRequisitionPath(requisitionId: number | string): string {
		return this.resolveAppPath(`/requisition/${requisitionId}`);
	}

	/**
	 * Returns the default home path for the current route namespace.
	 *
	 * @return {string} `/kiosk/requisitions` or `/requisitions`.
	 */
	public getDefaultHomePath(): string {
		return this.isKioskContext() ? '/kiosk/requisitions' : '/requisitions';
	}
}
